import { useState, useId } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle2, ArrowRight } from "lucide-react";
import { useT } from "@/i18n/I18nProvider";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const empty = { first_name: "", last_name: "", phone: "", email: "", message: "" };
const FIELDS = [
  { k: "first_name", label: "First name", type: "text", auto: "given-name" },
  { k: "last_name", label: "Last name", type: "text", auto: "family-name" },
  { k: "phone", label: "Phone", type: "tel", auto: "tel", minLength: 6 },
  { k: "email", label: "Email", type: "email", auto: "email" },
];

const submitEnquiry = (form, sourcePage) => axios.post(`${API}/enquiries`, { ...form, source_page: sourcePage });

const SuccessPanel = ({ dark, onReset }) => {
  const t = useT();
  return (
    <div role="status" aria-live="polite" className={`corner-tr-sm p-8 text-center ${dark ? "bg-white/5 text-white" : "bg-[var(--blue-tint)]"}`} data-testid="enquiry-success">
      <CheckCircle2 size={44} className="mx-auto text-[var(--coral)]" aria-hidden="true" />
      <h3 className={`mt-4 text-xl font-bold ${dark ? "!text-white" : ""}`}>{t("Thank you!")}</h3>
      <p className={`mt-2 text-sm ${dark ? "text-white/70" : "text-[var(--muted)]"}`}>{t("Your enquiry has been received. One of our team will contact you soon.")}</p>
      <button onClick={onReset} className={`btn mt-6 text-sm ${dark ? "btn-outline-light" : "btn-ghost"}`} data-testid="enquiry-send-another">{t("Send another")}</button>
    </div>
  );
};

const Required = () => <span aria-hidden="true" className="text-[var(--coral)]">*</span>;

export const EnquiryForm = ({ sourcePage = "home", dark = false }) => {
  const t = useT();
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const uid = useId();
  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const cls = `field ${dark ? "field-dark" : ""}`;
  const labelCls = `label ${dark ? "!text-white" : ""}`;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitEnquiry(form, sourcePage);
      setSent(true);
      setForm(empty);
      toast.success(t("Thank you! We've received your enquiry and will be in touch shortly."));
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(Array.isArray(detail) ? detail.map((d) => d.msg).join(", ") : t("Something went wrong. Please try again or call us."));
    } finally {
      setLoading(false);
    }
  };

  if (sent) return <SuccessPanel dark={dark} onReset={() => setSent(false)} />;

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="enquiry-form" aria-label={t("Quick enquiry form")}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {FIELDS.map((f) => (
          <div key={f.k}>
            <label htmlFor={`${uid}-${f.k}`} className={labelCls}>{t(f.label)} <Required /></label>
            <input id={`${uid}-${f.k}`} data-testid={`enquiry-${f.k.replace("_", "-")}`} className={cls} type={f.type} autoComplete={f.auto} minLength={f.minLength} required aria-required="true" value={form[f.k]} onChange={update(f.k)} />
          </div>
        ))}
      </div>
      <div>
        <label htmlFor={`${uid}-message`} className={labelCls}>{t("How can we help?")} <Required /></label>
        <textarea id={`${uid}-message`} data-testid="enquiry-message" className={`${cls} min-h-[120px] resize-y`} placeholder={t("Tell us a little about the support you're looking for")} required aria-required="true" value={form.message} onChange={update("message")} />
      </div>
      <button type="submit" disabled={loading} className="btn btn-coral w-full sm:w-auto" data-testid="enquiry-submit">
        {loading ? <Loader2 size={16} className="animate-spin" aria-hidden="true" /> : <ArrowRight size={16} aria-hidden="true" />} {t("Send enquiry")}
      </button>
    </form>
  );
};
